import { computed, inject, Injectable, signal } from '@angular/core';
import { Observable, of, tap } from 'rxjs';
import { AuthApiService } from '../api/auth-api.service';
import { IdentityUser, IAMUser } from '../models/user.model';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { toSignal } from '@angular/core/rxjs-interop';
import { Role } from '../enums/role.enum';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { LOGIN_METHOD_KEY, LoginMethod } from '../enums/login-method.enum';
@Injectable({ providedIn: 'root' })
export class AuthService {
  identityUser = signal<Nullable<IdentityUser>>(null);
  identityUserRole = signal<string | null>(null);
  isScreenLocked = signal(false);
  private _isLoggingOut = false;

  private _authApi = inject(AuthApiService);
  private _router = inject(Router);

  private _oidcSecurityService = inject(OidcSecurityService);
  private _userData = toSignal(this._oidcSecurityService.userData$);
  public user = computed<IAMUser | null>(
    () => this._userData()?.userData || null
  );
  public isAdministrator = computed(() => this._isOfRole(Role.Administrator));

  constructor() {
    const loginMethod = localStorage.getItem(LOGIN_METHOD_KEY);
    if(loginMethod === LoginMethod.Identity) {
      this.autoLogin();
    }
  }

  private _isOfRole(role: Role) {
    const userRole = this.user()?.role;
    const loginMethod = localStorage.getItem(LOGIN_METHOD_KEY);
    
    if (loginMethod === LoginMethod.IAM) {
      return Array.isArray(userRole)
        ? !!userRole?.includes(role)
        : userRole === role;
    } else if (loginMethod === LoginMethod.Identity) {
      return !!this.identityUser()?.roles?.includes(role);
    }
    return false;
  }

  autoLogin(): Observable<IdentityUser | null> {
    const user = this.identityUser();
    if (user) return of(user);

    const storedUser = this._getUserFromLocalStorage();

    if (!storedUser || !storedUser.token) {
      this._clearAuthState();
      return of(null);
    }

    this.identityUser.set(storedUser);
    return of(storedUser);
  }

  private _clearAuthState() {
    this._setUserInLocalStorage(null);
    this.identityUser.set(null);
    this._router.navigate(['/auth']);
  }

  private _getUserFromLocalStorage(): IdentityUser | null {
    const storedUser = localStorage.getItem(environment.USER_KEY);
    if (!storedUser) return null;

    return JSON.parse(storedUser);
  }

  private _setUserInLocalStorage(user: IdentityUser | null): void {
    if (!user) {
      localStorage.removeItem(environment.USER_KEY);
      return;
    }

    localStorage.setItem(environment.USER_KEY, JSON.stringify(user));
  }

  loginWithIAM() {
    this._authApi.loginWithIAM();
    localStorage.setItem(LOGIN_METHOD_KEY, LoginMethod.IAM);
  }

  logout() {
    // Prevent re-entrancy if logout is already in progress
    if (this._isLoggingOut) return;
    this._isLoggingOut = true;
    
    const loginMethod = localStorage.getItem(LOGIN_METHOD_KEY);
    
    // Call backend logout for audit logging only for Identity login (fire and forget)
    if (loginMethod === LoginMethod.Identity) {
      this._authApi.logoutFromIdentity().subscribe({ error: () => {} });
    }
    
    localStorage.removeItem(LOGIN_METHOD_KEY);
    this._authApi.logout();
    localStorage.removeItem(environment.USER_KEY);
    localStorage.removeItem(environment.USER_KEY_IAM);
    this._isLoggingOut = false;
    this._router.navigate(['/auth']);
  }

  loginWithIdentity(
    username: string,
    password: string
  ): Observable<IdentityUser> {
    return this._authApi.loginWithIdentity(username, password).pipe(
      tap((user) => {
        this._setUserInLocalStorage(user);
          localStorage.setItem(LOGIN_METHOD_KEY, LoginMethod.Identity);
        this.identityUser.set(user);
      })
    );
  }
}
